import type { ScheduleTimeData } from "@qendo/schedule";
import type { DateArg } from "date-fns";
import { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import { Button } from "~/components/Button";
import { TimePicker } from "~/components/form/date-picker/TimePicker";
import { NumberInput } from "~/components/form/NumberInput";
import { useTheme } from "~/providers/ThemeProvider";
import { itemWidth, setWidthOnLayout } from "~/util/layout";
import type { NumericString } from "~/util/string";

export type ScheduleTimeRowProps = {
    value?: ScheduleTimeData | null | undefined;
    onUpdate?: (value: ScheduleTimeData) => void;
    disabled?: boolean;
    onRemove?: () => void;
    dosageLabel?: string | null | undefined;
    style?: StyleProp<View>;
};

export const BUTTON_SIZE = 26;
export const ITEM_GAP = 12;

export const ScheduleTimeRow = ({ value, onUpdate, disabled, onRemove, dosageLabel }: ScheduleTimeRowProps) => {
    const { border } = useTheme();

    const with_dose = Boolean(dosageLabel);
    const with_remove = Boolean(onRemove);
    const time = value?.time ?? null;
    const amount = with_dose ? (value?.amount ?? null) : undefined;
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const line_width = with_remove ? width - (BUTTON_SIZE + ITEM_GAP) : width;
    const element_width = itemWidth(line_width, with_dose ? 2 : 1, ITEM_GAP);
    const [dose, setDose] = useState<NumericString | null>(amount ?? null);

    const current = { time, amount } as ScheduleTimeData;

    // biome-ignore lint/correctness/useExhaustiveDependencies(dose): upstream
    useEffect(() => {
        if (with_dose && amount && dose !== amount) {
            setDose(amount);
        }
    }, [with_dose, amount]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(onUpdate): setter
    // biome-ignore lint/correctness/useExhaustiveDependencies(time): upstream
    // biome-ignore lint/correctness/useExhaustiveDependencies(amount): upstream
    useEffect(() => {
        if (time && onUpdate) {
            if (with_dose) {
                if (amount !== dose) {
                    onUpdate({ time, amount: dose });
                }
            } else {
                onUpdate({ time });
            }
        }
    }, [with_dose, dose]);

    const onSetTime = (selected: DateArg<Date> | null) => {
        if (selected && onUpdate) {
            onUpdate({
                ...current,
                time: selected,
            });
        }
    };

    return (
        <View style={[styles.container, border.bottom_secondary]} onLayout={onLayout}>
            {with_remove && (
                <Button
                    icon="minus"
                    size="sm"
                    variant="destructive"
                    disabled={disabled}
                    onPress={onRemove}
                    style={styles.button}
                />
            )}
            <TimePicker
                value={time}
                onDateSelected={onSetTime}
                disabled={disabled}
                label="Time"
                hideLabel
                style={{ width: element_width }}
            />
            {with_dose && (
                <NumberInput
                    value={dose}
                    disabled={disabled}
                    onChangeText={setDose}
                    name="Amount"
                    hideLabel
                    containerStyle={{ width: element_width }}
                    right={dosageLabel}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        gap: ITEM_GAP,
        alignItems: "center",
        paddingBottom: 4,
        borderBottomWidth: 1,
    },
    button: {
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        flexShrink: 0,
    },
});
